import { request } from "./http";
import { DEFAULT_REACTION_KIND, normalizeReactionKind } from "../ui/reactions/reactionKinds";
import { buildReactionQuery, buildReactionTogglePayload, normalizeReactionResponse as normalizeUnifiedReactionResponse, REACTION_TARGET_TYPES } from "../ui/reactions/reactionModel";

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function unwrapOk(raw) {
  const obj = asObject(raw);
  if (Object.prototype.hasOwnProperty.call(obj, "item")) return obj.item;
  if (Object.prototype.hasOwnProperty.call(obj, "items")) return obj.items;
  return obj;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeAuthor(raw) {
  const a = asObject(raw);
  return {
    id: toId(a.id),
    name: String(a.name || "Пользователь"),
    avatarUrl: String(a.avatarUrl || ""),
    anonymous: Boolean(a.anonymous),
  };
}

function normalizeSection(raw) {
  const section = asObject(raw);
  return {
    id: toId(section.id),
    title: String(section.title || ""),
    slug: String(section.slug || ""),
    description: String(section.description || ""),
    sortOrder: toNumber(section.sortOrder, 0),
    topicsCount: Math.max(0, toNumber(section.topicsCount, 0)),
    allowAnonymousTopics: Boolean(section.allowAnonymousTopics),
    allowAnonymousComments: Boolean(section.allowAnonymousComments),
  };
}

function normalizeTopic(raw) {
  const topic = asObject(raw);
  const section = asObject(topic.section);
  const topicAuthor = asObject(topic.author);
  const myReactionKind = normalizeReactionKind(
    topic.myReactionKind || topic.reactionKindByMe || (topic.likedByMe ? DEFAULT_REACTION_KIND : null),
    null
  );
  return {
    id: toId(topic.id),
    sectionId: toId(topic.sectionId) ?? toId(section.id),
    sectionTitle: String(topic.sectionTitle || section.title || ""),
    title: String(topic.title || ""),
    content: String(topic.content || topic.text || topic.body || ""),
    author: normalizeAuthor(topic.author),
    pinned: Boolean(topic.pinned),
    locked: Boolean(topic.locked),
    viewsCount: Math.max(0, toNumber(topic.viewsCount, 0)),
    commentsCount: Math.max(0, toNumber(topic.commentsCount, 0)),
    reactionsCount: Math.max(0, toNumber(topic.reactionsCount, 0)),
    createdWhen: topic.createdWhen || null,
    lastModified: topic.lastModified || null,
    likedByMe: Boolean(topic.likedByMe || myReactionKind),
    myReactionKind,
    mine: Boolean(topic.mine || topic.isMine || topic.ownedByMe || topicAuthor.isMe),
    unread: Boolean(topic.unread || topic.hasUnread || topic.isUnread),
  };
}

function normalizeComment(raw) {
  const comment = asObject(raw);
  const topic = asObject(comment.topic);
  const parent = asObject(comment.parent);
  const myReactionKind = normalizeReactionKind(
    comment.myReactionKind || comment.reactionKindByMe || (comment.likedByMe ? DEFAULT_REACTION_KIND : null),
    null
  );
  return {
    id: toId(comment.id),
    topicId: toId(comment.topicId) ?? toId(topic.id),
    topicTitle: String(comment.topicTitle || topic.title || ""),
    parentCommentId: toId(comment.parentCommentId) ?? toId(comment.parentId) ?? toId(parent.id),
    depth: Math.max(0, toNumber(comment.depth, toNumber(comment.level, 0))),
    content: String(comment.content || comment.text || comment.body || ""),
    author: normalizeAuthor(comment.author),
    reactionsCount: Math.max(0, toNumber(comment.reactionsCount, 0)),
    createdWhen: comment.createdWhen || null,
    likedByMe: Boolean(comment.likedByMe || myReactionKind),
    myReactionKind,
  };
}

function normalizeReactionResponse(raw) {
  const data = asObject(unwrapOk(raw));
  const unified = normalizeUnifiedReactionResponse(data, DEFAULT_REACTION_KIND);
  const myReactionKind = normalizeReactionKind(
    unified.myReactionKind || data.myReactionKind || data.reactionKindByMe || (data.liked ? DEFAULT_REACTION_KIND : null),
    null
  );
  return {
    liked: Boolean(unified.liked || myReactionKind),
    reacted: Boolean(unified.reacted || myReactionKind),
    reactionsCount: Math.max(0, toNumber(unified.reactionsCount ?? data.reactionsCount, 0)),
    myReactionKind,
  };
}

function normalizePage(raw, mapper) {
  const page = asObject(raw);
  const rawContent = Array.isArray(raw)
    ? raw
    : Array.isArray(page.content)
    ? page.content
    : Array.isArray(page.items)
    ? page.items
    : [];
  const content = rawContent.map(mapper);
  const size = Math.max(1, toNumber(page.size, rawContent.length || 20));
  const totalElements = Math.max(0, toNumber(page.totalElements, rawContent.length));
  const fallbackTotalPages = Math.max(1, Math.ceil(totalElements / size));
  const number = Math.max(0, toNumber(page.number, toNumber(page.page, 0)));
  const totalPages = Math.max(1, toNumber(page.totalPages, fallbackTotalPages));
  return {
    content,
    number,
    size,
    totalElements,
    totalPages,
    first: Object.prototype.hasOwnProperty.call(page, "first") ? Boolean(page.first) : number <= 0,
    last: Object.prototype.hasOwnProperty.call(page, "last")
      ? Boolean(page.last)
      : number >= totalPages - 1,
    empty: Object.prototype.hasOwnProperty.call(page, "empty")
      ? Boolean(page.empty)
      : content.length === 0,
  };
}

function toQuery(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    sp.set(key, String(value));
  });
  const raw = sp.toString();
  return raw ? `?${raw}` : "";
}

function canTryFallback(err) {
  const status = Number(err?.status ?? err?.response?.status ?? 0);
  return status === 404 || status === 405 || status === 501 || status === 403;
}

async function requestFirstAvailable(paths = [], makeCall) {
  let lastError = null;
  for (const path of paths) {
    try {
      return await makeCall(path);
    } catch (error) {
      if (!canTryFallback(error)) throw error;
      lastError = error;
    }
  }
  if (lastError) throw lastError;
  throw new Error("No available forum endpoint");
}

export const forumApi = {
  listSections: async () => {
    const raw = await request("/api/forum/sections");
    const data = unwrapOk(raw);
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data.items)
      ? data.items
      : Array.isArray(data.content)
      ? data.content
      : [];
    return items.map(normalizeSection).filter((x) => x.id != null);
  },

  listTopics: async ({ sectionId, page = 0, size = 20 } = {}) => {
    const raw = await request(
      `/api/forum/topics${toQuery({ sectionId, page, size })}`
    );
    return normalizePage(unwrapOk(raw), normalizeTopic);
  },

  getTopic: async (topicId, { incrementView = true } = {}) => {
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}${toQuery({
        incrementView,
      })}`
    );
    return normalizeTopic(unwrapOk(raw));
  },

  listComments: async (topicId) => {
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}/comments`
    );
    const data = unwrapOk(raw);
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data.content)
      ? data.content
      : Array.isArray(data.items)
      ? data.items
      : [];
    return items.map(normalizeComment).filter((x) => x.id != null);
  },

  createTopic: async (payload = {}) => {
    const raw = await request("/api/forum/topics", {
      method: "POST",
      json: payload,
    });
    return normalizeTopic(unwrapOk(raw));
  },

  createComment: async (topicId, payload = {}) => {
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}/comments`,
      {
        method: "POST",
        json: payload,
      }
    );
    return normalizeComment(unwrapOk(raw));
  },

  toggleTopicReaction: async (topicId, kind = DEFAULT_REACTION_KIND) => {
    const reactionKind = normalizeReactionKind(kind, DEFAULT_REACTION_KIND);
    const query = buildReactionQuery({ reaction: reactionKind });
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}/reactions/toggle?${query}`,
      {
        method: "POST",
        json: buildReactionTogglePayload({
          reaction: reactionKind,
          targetType: REACTION_TARGET_TYPES.FORUM_TOPIC,
          targetId: topicId,
        }),
      }
    );
    return normalizeReactionResponse(raw);
  },

  toggleCommentReaction: async (commentId, kind = DEFAULT_REACTION_KIND) => {
    const reactionKind = normalizeReactionKind(kind, DEFAULT_REACTION_KIND);
    const query = buildReactionQuery({ reaction: reactionKind });
    const raw = await request(
      `/api/forum/comments/${encodeURIComponent(commentId)}/reactions/toggle?${query}`,
      {
        method: "POST",
        json: buildReactionTogglePayload({
          reaction: reactionKind,
          targetType: REACTION_TARGET_TYPES.FORUM_COMMENT,
          targetId: commentId,
        }),
      }
    );
    return normalizeReactionResponse(raw);
  },

  reportTopic: async (topicId, payload = {}) => {
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}/report`,
      {
        method: "POST",
        json: payload,
      }
    );
    return unwrapOk(raw);
  },

  reportComment: async (commentId, payload = {}) => {
    const raw = await request(
      `/api/forum/comments/${encodeURIComponent(commentId)}/report`,
      {
        method: "POST",
        json: payload,
      }
    );
    return unwrapOk(raw);
  },

  setPinned: async (topicId, pinned) => {
    const action = pinned ? "pin" : "unpin";
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}/${action}`,
      {
        method: "POST",
      }
    );
    return normalizeTopic(unwrapOk(raw));
  },

  setLocked: async (topicId, locked) => {
    const action = locked ? "lock" : "unlock";
    const raw = await request(
      `/api/forum/topics/${encodeURIComponent(topicId)}/${action}`,
      {
        method: "POST",
      }
    );
    return normalizeTopic(unwrapOk(raw));
  },

  listMyTopics: async ({ page = 0, size = 20 } = {}) => {
    const raw = await requestFirstAvailable(
      ["/api/me/content/forum/topics", "/api/me/forum/topics"],
      (path) => request(`${path}${toQuery({ page, size })}`)
    );
    return normalizePage(unwrapOk(raw), normalizeTopic);
  },

  listMyComments: async ({ page = 0, size = 20 } = {}) => {
    const raw = await requestFirstAvailable(
      ["/api/me/content/forum/comments", "/api/me/forum/comments"],
      (path) => request(`${path}${toQuery({ page, size })}`)
    );
    return normalizePage(unwrapOk(raw), normalizeComment);
  },
};
